import React, {useEffect, useMemo, useRef, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {authActions} from "../store";
import {useTranslation, Trans} from "react-i18next";
import {Card, Typography, Button, Form} from "antd";
import {GoogleOutlined} from "@ant-design/icons";

import {getProvidersUrls} from "../utils/auth";
import {PROVIDER} from "../constants/auth";

import {validateEmail} from "../utils/InputUtils/EmailValidate";

import AuthHeader from "../components/Header/AuthHeader";
import ValidatedInput from "../components/Inputs/ValidatedInput";
import PasswordField from "../components/Inputs/PasswordField";
import PrivacyCheckbox from "../components/CheckBoxs/PrivacyCheckbox";
import TooltipWrapper from "../components/Wrappers/TooltipWrapper";
import WelcomeTutorialModal from "../components/modals/WelcomeTutorialModal";
import {LanguageHeader} from "../components/LanguageHeader/index"

import axios from "axios";

import xIcon from "./icons/linkedin-icon.svg"; // Replace with path to your Apple icon
import fbIcon from "./icons/facebook-icon.svg";
import instIcon from "./icons/instagram-logo.png";


import Public_Offer_IdeaSocial_ru from "../files/Public_Offer_IdeaSocial_ru.pdf";
import Public_Offer_IdeaSocial_en from "../files/Public_Offer_IdeaSocial_en.pdf";
import payment_ru from "../files/payment_ru.pdf";
import payment_en from "../files/payment_en.pdf";
import privacy_ru from "../files/Privacy_Policy_IdeaSocial_ru.pdf";
import privacy_en from "../files/Privacy_Policy_IdeaSocial_en.pdf";

import {message} from "antd";
import {getUserCoordinates} from "../utils/getUserCoordinates";
import {fetchWrapper} from "../helpers";

const {Title, Text, Link: ALink} = Typography;

const SignInPage = () => {
    const navigate = useNavigate();
    const {t, i18n} = useTranslation();
    const dispatch = useDispatch();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [accepted, setAccepted] = useState(false);
    const [emailState, setEmailState] = useState("initial");
    const [emailMessage, setEmailMessage] = useState("");

    const [isWelcomeTutorialModalOpen, setIsWelcomeTutorialModalOpen] = useState(false);
    const user = useSelector((obj) => obj.auth.user);

    // NEW: Track when we should visually highlight the consent area (initially invisible).
    const [showConsentError, setShowConsentError] = useState(false);

    // NEW: Ref to the consent wrapper to focus/scroll into view for accessibility.
    const consentRef = useRef(null);

    const termsByLang = {
        ru: Public_Offer_IdeaSocial_ru,
        en: Public_Offer_IdeaSocial_en,
        kk: Public_Offer_IdeaSocial_en,
    };

    const privacyByLang = {
        ru: privacy_ru,
        en: privacy_en,
        kk: privacy_en,
    };

    const resolveDocLang = (ln) => (ln || "en").toLowerCase().split("-")[0];

    // Helper: centralized consent error indication with focus + toast.
    const indicateConsentError = () => {
        setShowConsentError(true); // show red border only after a failed attempt
        message.error(t("signup.acceptPolicy") || "Примите политику и правила");
        // Defer to ensure DOM is painted before focusing/scrolling
        setTimeout(() => {
            // Scroll the consent block into view and focus the checkbox (if available)
            consentRef.current?.scrollIntoView({behavior: "smooth", block: "center"});
            const checkboxEl = consentRef.current?.querySelector('input[type="checkbox"]');
            checkboxEl?.focus?.({preventScroll: true});
        }, 0);
    };

    // Handler for form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        // NEW: Intercept submit if ToS/PP not accepted; show visual/error state.
        if (!accepted) {
            indicateConsentError();
            return;
        }

        // Validate email before submission
        const emailValidationError = validateEmail(email);
        if (emailValidationError) {
            setEmailState("error");
            setEmailMessage(emailValidationError);
            return; // Don't submit if invalid
        }

        // Proceed with login
        dispatch(authActions.login({username: email, password}));
        // Implement your sign-up logic here
    };

    const onSocialLogin = (key) => {
        localStorage.setItem("provider", key?.toLowerCase());
        fetchWrapper
            .get(`${process.env.REACT_APP_API_URL}/auth/special-url?sn=${key?.toLowerCase()}`)
            .then((res) => {
                if (res.redirect_url) {
                    window.location = res.redirect_url;
                } else {
                    window.location = getProvidersUrls()[key];
                }
            })
            .catch((err) => {
                console.error(err);
                window.location = getProvidersUrls()[key];
            });
    };

    const showWelcomeTutorialModal = () => setIsWelcomeTutorialModalOpen(true);

    const handleEmailChange = (e) => {
        const newEmail = e.target.value.toLowerCase();

        setEmail(newEmail);

        if (!newEmail) {
            setEmailState("initial");
            setEmailMessage(t("validation.enterEmail"));
        } else {
            // Don't validate while typing, just clear initial state
            setEmailState("typing");
            setEmailMessage("");
        }
    };

    const handleEmailBlur = () => {
        if (!email) {
            setEmailState("initial");
            setEmailMessage(t("validation.enterEmail"));
            return;
        }

        const error = validateEmail(email);
        if (error) {
            setEmailState("error");
            setEmailMessage(t(error)); // Or use translation: t(error)
        } else {
            setEmailState("success");
            setEmailMessage(t("validation.emailValid"));
        }
    };


    useEffect(() => {
        if (user) {
            window.location = "/";
        }
    }, [user]);

    // NEW: Clear visual error as soon as the user accepts.
    useEffect(() => {
        if (accepted && showConsentError) setShowConsentError(false);
    }, [accepted, showConsentError]);

    const currentLang = resolveDocLang(i18n.language);

    const isFormValid = useMemo(() => !validateEmail(email), [email]);

    // CHANGED: Do NOT disable the main submit based on "accepted".
    // This enables click, so we can show the red border & helpful error when unchecked.
    const submitDisabled = !isFormValid || !password;

    // CHANGED: Hint no longer talks about policy for the primary button (policy handled on-click now).
    const submitHint = !isFormValid
        ? (t("validation.fixValid") || "Проверьте поля формы")
        : (!password ? (t("validation.enterPassword") || "Введите пароль") : "");

    // NEW: Memoized class for the consent wrapper (perf + clarity).
    const consentWrapperClass = useMemo(
        () =>
            (showConsentError && !accepted)
                ? "ring-2 ring-red-500 rounded-md pl-1 pr-2 transition-shadow duration-150 m-2"
                : "",
        [showConsentError, accepted]
    );

    return (
        <>
            <LanguageHeader/>
            <div className="min-h-screen bg-bgPage flex flex-col items-center px-4 pt-10 pb-6">
                <AuthHeader
                    active="login"
                    signupText={t("signup.signUp")}
                    loginText={t("signin.signIn")}
                    onSignupClick={() => navigate("/sign-up")}
                    onLoginClick={() => {
                    }}
                />

                <Title level={2} className="my-3 !font-bold" style={{color: '#8932F6'}}>
                    {t("signin.login")}
                </Title>
                <Text className="text-textWeak mb-5">{t("signin.submitSign")}</Text>

                <Card className="w-full max-w-[440px] rounded-lg shadow-card" bordered={false}>
                    <Form layout="vertical" className="text-left" onSubmitCapture={handleSubmit}>
                        <ValidatedInput
                            label={t("signin.emailLabel")}
                            type="email"
                            placeholder="user@example.com"
                            value={email}
                            onChange={handleEmailChange}
                            onBlur={handleEmailBlur}
                            state={emailState}
                            message={emailMessage}
                            name="email"
                            autoComplete="email"
                            showSuccess
                        />

                        <Form.Item
                            label={t("signin.passwordLabel")}
                        >
                            <PasswordField
                                value={password}
                                placeholder={t("signin.inputPassword")}
                                className={`h-[44px] rounded-[12px]`}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <div className="mt-2 text-right">
                                <Link to="/forgot-password" className="text-sm">
                                    {t("signin.forgotPassword")}
                                </Link>
                            </div>
                        </Form.Item>

                        {/* NEW: Wrap consent block to apply conditional red border + accessibility flags */}
                        <div
                            ref={consentRef}
                            className={consentWrapperClass}
                            aria-invalid={showConsentError && !accepted ? true : undefined}
                            role={showConsentError && !accepted ? "alert" : undefined}
                        >
                            <PrivacyCheckbox
                                checked={accepted}
                                onChange={setAccepted}
                                termsPath={termsByLang[currentLang] || termsByLang.en}
                                privacyPath={privacyByLang[currentLang] || privacyByLang.en}
                            />
                            {/* Optional inline helper when error is visible */}
                            {(showConsentError && !accepted) && (
                                <div className="text-red-600 text-sm mt-1">
                                    {t("signup.acceptPolicy") || "Примите политику и правила"}
                                </div>
                            )}
                        </div>

                        <div className="space-y-4">
                            {/* Tooltip remains for other validation errors, but no longer about policy here */}
                            <TooltipWrapper disabled={!submitDisabled} title={submitHint}>
                                <Button
                                    htmlType="submit"
                                    type="primary"
                                    size="large"
                                    className="rounded-full h-[44px] bg-brand border-brand"
                                    block
                                    // IMPORTANT: not disabled by policy anymore
                                    disabled={submitDisabled}
                                    onClick={(e) => {
                                        // Safety: also catch direct clicks if the form submit did not trigger
                                        if (!accepted) {
                                            e.preventDefault();
                                            indicateConsentError();
                                        }
                                    }}
                                >
                                    {t("signin.continue")}
                                </Button>
                            </TooltipWrapper>

                            {/* Social login: clickable even when not accepted; we intercept and show error */}
                            <TooltipWrapper
                                // Keep tooltip to hint the need for policy when not accepted
                                disabled={accepted}
                                title={t("signup.acceptPolicy") || "Примите политику и правила"}
                            >
                                <Button
                                    size="large"
                                    className="rounded-full h-[44px]"
                                    icon={<GoogleOutlined/>}
                                    block
                                    // IMPORTANT: do NOT disable based on "accepted"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!accepted) {
                                            // Intercept, highlight consent, and do NOT proceed
                                            indicateConsentError();
                                            return;
                                        }
                                        onSocialLogin(PROVIDER.GOOGLE);
                                    }}
                                >
                                    {t("signin.signInGoogle")}
                                </Button>
                            </TooltipWrapper>
                        </div>
                    </Form>
                </Card>

                <div className="mt-6 text-center flex flex-col gap-3">
                    <Text>
                        {t("signin.dontHaveAccount")}{" "}
                        <ALink onClick={() => navigate("/sign-up")}>{t("signup.signUp")}</ALink>
                    </Text>

                    <button
                        className="text-sm hover:text-violet-600 font-semibold hover:underline"
                        onClick={showWelcomeTutorialModal}
                    >
                        {t("signin.howThatWorkingBtn")}
                    </button>

                    <Text type="secondary">{t("signup.copyrights")}</Text>
                </div>
            </div>

            <WelcomeTutorialModal
                open={isWelcomeTutorialModalOpen}
                onClose={() => setIsWelcomeTutorialModalOpen(false)}
                onGo={() => navigate("/how-to-work")}
                rememberKey="isHideWelcomeTutorialModal"
            />
        </>
    );
};

export default SignInPage;
